import { FC } from 'react';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { FindStopPlaceInfoFragment } from '../../../../../generated/graphql';
import { PathValue, routeDetails } from '../../../../../router/routeDetails';
import { LocatorButton } from '../../../../common/Buttons';
import { ResultSelection } from '../../Types';
import { BatchUpdateSelection, areAllStopsSelected } from '../../Utils';
import { SelectAllCheckbox } from '../SelectAllCheckbox';
import { ActionMenu } from './ActionMenu/ActionMenu';
import { OpenDetails } from './ActionMenu/OpenDetailsPage';
import { ShowOnMap } from './ActionMenu/ShowOnMap';

const testIds = {
  stopAreaLabel: 'StopPlaceSearch::label',
  stopAreaLink: 'StopPlaceSearch::link',
  locatorButton: 'StopPlaceSearch::locatorButton',
  showOnMap: 'StopPlaceSearch::showOnMap',
  showStopPlaceDetails: 'StopPlaceSearch::showStopPlaceDetails',
  selectAllStopPlaceStops: (privateCode: string) =>
    `StopPlaceSearch::selectAll::${privateCode}`,
};

type ImplementationProps = {
  readonly colorClasses: string;
  readonly onShowOnMap: () => void;
  readonly path: PathValue;
  readonly linkTitle: string;
  readonly linkContent: string;
  readonly showOnMapTooltip: string;
  readonly menuShowDetails: string;
  readonly menuShowOnMap: string;
};

type StopPlaceHeaderContentProps = {
  readonly className?: string;
  readonly observationDate: Temporal.PlainDate;
  readonly implementationProps: ImplementationProps;
  readonly isRounded: boolean;
  readonly stopPlace: FindStopPlaceInfoFragment;
};

const StopPlaceHeaderContent: FC<StopPlaceHeaderContentProps> = ({
  className,
  implementationProps,
  isRounded,
  observationDate,
  stopPlace,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center gap-4 rounded-t-xl border-x border-t p-4',
        isRounded ? 'rounded-b-xl border-b' : '',
        implementationProps.colorClasses,
        className,
      )}
    >
      <Link
        to={routeDetails[implementationProps.path].getLink(
          stopPlace.private_code,
          {
            observationDate,
          },
        )}
        data-testid={testIds.stopAreaLink}
        title={implementationProps.linkTitle}
      >
        <h3 data-testid={testIds.stopAreaLabel}>
          {implementationProps.linkContent}
        </h3>
      </Link>

      <div className="grow" />

      <LocatorButton
        onClick={implementationProps.onShowOnMap}
        tooltipText={implementationProps.showOnMapTooltip}
        testId={testIds.locatorButton}
      />

      <ActionMenu>
        <OpenDetails
          details={implementationProps.path}
          observationDate={observationDate}
          privateCode={stopPlace.private_code}
          testId={testIds.showStopPlaceDetails}
          text={implementationProps.menuShowDetails}
        />
        <ShowOnMap
          onClick={implementationProps.onShowOnMap}
          testId={testIds.showOnMap}
          text={implementationProps.menuShowOnMap}
        />
      </ActionMenu>
    </div>
  );
};

type SelectAllStopPlaceStopsProps = {
  readonly onBatchUpdateSelection: BatchUpdateSelection;
  readonly selection: ResultSelection;
  readonly stopIds: ReadonlyArray<string>;
  readonly stopPlace: FindStopPlaceInfoFragment;
};

const SelectAllStopPlaceStops: FC<SelectAllStopPlaceStopsProps> = ({
  onBatchUpdateSelection,
  selection,
  stopIds,
  stopPlace,
}) => {
  const hasNoStops = stopIds.length === 0;
  const allSelected = hasNoStops
    ? false
    : areAllStopsSelected(selection, stopIds);

  const onToggleSelectAll = () =>
    onBatchUpdateSelection((actualSelection) => {
      if (areAllStopsSelected(actualSelection, stopIds)) {
        return { exclude: stopIds };
      }

      return { include: stopIds };
    });

  return (
    <SelectAllCheckbox
      className="ml-px"
      allSelected={allSelected}
      disabled={hasNoStops}
      onToggleSelectAll={onToggleSelectAll}
      testId={testIds.selectAllStopPlaceStops(
        stopPlace.private_code ?? stopPlace.id,
      )}
    />
  );
};

type StopPlaceHeaderProps = StopPlaceHeaderContentProps &
  SelectAllStopPlaceStopsProps;

export type StopPlaceHeaderPublicPropsProps = Omit<
  StopPlaceHeaderProps,
  'implementationProps'
>;

export const StopPlaceHeader: FC<StopPlaceHeaderProps> = ({
  className,
  implementationProps,
  observationDate,
  onBatchUpdateSelection,
  stopIds,
  stopPlace,
  selection,
  isRounded,
}) => {
  return (
    <div className={twMerge('flex items-center gap-5', className)}>
      <SelectAllStopPlaceStops
        onBatchUpdateSelection={onBatchUpdateSelection}
        selection={selection}
        stopIds={stopIds}
        stopPlace={stopPlace}
      />
      <StopPlaceHeaderContent
        className="grow"
        implementationProps={implementationProps}
        isRounded={isRounded}
        observationDate={observationDate}
        stopPlace={stopPlace}
      />
    </div>
  );
};
